import { Network } from '@capacitor/network'
import type { URLOpenListenerEvent } from '@capacitor/app'
import { App } from '@capacitor/app'
import { useAppStateStore } from '@/stores/AppStateStore'

class AppStateListenerManager {
  private networkActiveListeners: (() => void)[] = []
  private networkInactiveListeners: (() => void)[] = []
  private appActiveListeners: (() => void)[] = []
  private appInactiveListeners: (() => void)[] = []
  private appUrlOpenListeners: ((url: URL) => void)[] = []

  private networkListenerRegistered = false
  private appStateListenerRegistered = false
  private appUrlOpenListenerRegistered = false

  private setupNetworkListener() {
    if (this.networkListenerRegistered) return

    // networkStatusChange gets triggered (at least on Android) whenever the strength of the network connection changes
    // this can happen a lot even in WLAN environments, so we need to make sure that we only trigger the callback when
    // the network connection actually becomes active
    void Network.addListener('networkStatusChange', (status) => {
      const previousNetworkStatus = useAppStateStore().networkIsActive
      useAppStateStore().networkIsActive = status.connected
      console.log('Network status changed', status)

      if (status.connected && previousNetworkStatus !== status.connected) {
        console.log('afterNetworkBecomesActive')
        this.networkActiveListeners.forEach((fn) => fn())
      }

      if (!status.connected) {
        console.log('afterNetworkBecomesInactive')
        this.networkInactiveListeners.forEach((fn) => fn())
      }
    })

    this.networkListenerRegistered = true
  }

  private setupAppStateListener() {
    if (this.appStateListenerRegistered) return

    void App.addListener('appStateChange', (state) => {
      console.log('App state changed', state)
      void Network.getStatus().then(
        (status) => (useAppStateStore().networkIsActive = status.connected),
      )

      if (state.isActive) {
        void Network.getStatus().then(
          (status) => (useAppStateStore().networkIsActive = status.connected),
        )

        console.log('afterAppBecomesActive')
        this.appActiveListeners.forEach((fn) => fn())
      } else {
        useAppStateStore().networkIsActive = false
        console.log('afterAppBecomesInactive')
        this.appInactiveListeners.forEach((fn) => fn())
      }
    })

    this.appStateListenerRegistered = true
  }

  private setupAppUrlOpenListener() {
    if (this.appUrlOpenListenerRegistered) return

    void App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
      console.log('App opened with URL: ', event.url)
      void Network.getStatus().then(
        (status) => (useAppStateStore().networkIsActive = status.connected),
      )
      const url = new URL(event.url)
      this.appUrlOpenListeners.forEach((fn) => fn(url))
    })

    this.appUrlOpenListenerRegistered = true
  }

  addNetworkActiveListener(fn: () => void): () => void {
    this.networkActiveListeners.push(fn)
    this.setupNetworkListener()
    return fn
  }

  removeNetworkActiveListener(fn: () => void) {
    const index = this.networkActiveListeners.indexOf(fn)
    if (index > -1) {
      this.networkActiveListeners.splice(index, 1)
    }
  }

  addNetworkInactiveListener(fn: () => void): () => void {
    this.networkInactiveListeners.push(fn)
    this.setupNetworkListener()
    return fn
  }

  removeNetworkInactiveListener(fn: () => void) {
    const index = this.networkInactiveListeners.indexOf(fn)
    if (index > -1) {
      this.networkInactiveListeners.splice(index, 1)
    }
  }

  addAppActiveListener(fn: () => void): () => void {
    this.appActiveListeners.push(fn)
    this.setupAppStateListener()
    return fn
  }

  removeAppActiveListener(fn: () => void) {
    const index = this.appActiveListeners.indexOf(fn)
    if (index > -1) {
      this.appActiveListeners.splice(index, 1)
    }
  }

  addAppInactiveListener(fn: () => void): () => void {
    this.appInactiveListeners.push(fn)
    this.setupAppStateListener()
    return fn
  }

  removeAppInactiveListener(fn: () => void) {
    const index = this.appInactiveListeners.indexOf(fn)
    if (index > -1) {
      this.appInactiveListeners.splice(index, 1)
    }
  }

  addAppUrlOpenListener(fn: (url: URL) => void): (url: URL) => void {
    this.appUrlOpenListeners.push(fn)
    this.setupAppUrlOpenListener()
    return fn
  }

  removeAppUrlOpenListener(fn: (url: URL) => void) {
    const index = this.appUrlOpenListeners.indexOf(fn)
    if (index > -1) {
      this.appUrlOpenListeners.splice(index, 1)
    }
  }
}

// Create singleton instance
const appStateListenerManager = new AppStateListenerManager()

// Export the manager methods
export const addNetworkActiveListener = (fn: () => void): (() => void) =>
  appStateListenerManager.addNetworkActiveListener(fn)
export const removeNetworkActiveListener = (fn: () => void) =>
  appStateListenerManager.removeNetworkActiveListener(fn)
export const addNetworkInactiveListener = (fn: () => void): (() => void) =>
  appStateListenerManager.addNetworkInactiveListener(fn)
export const removeNetworkInactiveListener = (fn: () => void) =>
  appStateListenerManager.removeNetworkInactiveListener(fn)
export const addAppActiveListener = (fn: () => void): (() => void) =>
  appStateListenerManager.addAppActiveListener(fn)
export const removeAppActiveListener = (fn: () => void) =>
  appStateListenerManager.removeAppActiveListener(fn)
export const addAppInactiveListener = (fn: () => void): (() => void) =>
  appStateListenerManager.addAppInactiveListener(fn)
export const removeAppInactiveListener = (fn: () => void) =>
  appStateListenerManager.removeAppInactiveListener(fn)
export const addAppUrlOpenListener = (
  fn: (url: URL) => void,
): ((url: URL) => void) => appStateListenerManager.addAppUrlOpenListener(fn)
export const removeAppUrlOpenListener = (fn: (url: URL) => void) =>
  appStateListenerManager.removeAppUrlOpenListener(fn)

// Legacy exports for backward compatibility (deprecated)
export const afterNetworkBecomesActive = (fn: () => void) =>
  addNetworkActiveListener(fn)
export const afterNetworkBecomesInactive = (fn: () => void) =>
  addNetworkInactiveListener(fn)
export const afterAppBecomesActive = (fn: () => void) =>
  addAppActiveListener(fn)
export const afterAppBecomesInactive = (fn: () => void) =>
  addAppInactiveListener(fn)
export const afterAppUrlOpen = (fn: (url: URL) => void) =>
  addAppUrlOpenListener(fn)
